// Where a mode opens when the viewer has not asked for a page.
//
// Teacher mode has one answer. Admin mode does not: `/admin/overview` is gated
// on `read:dashboards` or the `read:all` sentinel, and CSR, Finance and
// Marketing hold neither, so pointing every admin-mode viewer there would land
// three roles on Unauthorized. The list below is walked in order and the first
// route the viewer can actually open wins.
//
// Order, and why:
//
//   1. /admin/overview    — the organizational home, for anyone who can read it.
//   2. /admin/bookings    — a CSR's primary surface, gated the same as the
//                           route itself, so landing here never contradicts
//                           reaching it.
//   3. /admin/customers   — a CSR's other primary surface.
//   4. /admin/gift-cards  — narrower than the above (CSR-specific; RD, Finance
//                           and Marketing are deliberately excluded), so it
//                           only matters once the broader routes are ruled out.
//   5. /admin/marketing   — a Marketing viewer's home. The route carries no
//                           guard of its own, so this uses `read:promotions` —
//                           the nearest grant tied to that domain — as a proxy.
//   6. /admin/staff       — final catch-all, and unguarded, which is what
//                           guarantees this list terminates. Its Wage Report
//                           tab is also the one dollars-facing surface behind
//                           no gate, which makes it a reasonable landing for a
//                           Finance viewer specifically.
import { Permission, type PermissionString } from '../../types/permissions';

import { MODE_HOME, type ViewMode } from './viewMode';

/** `usePermissions().hasAnyPermission`. Named because it is threaded through
 *  the nav resolvers rather than read once at a redirect. */
export type HasAnyPermission = (...perms: PermissionString[]) => boolean;

interface ModeLandingRoute {
  path: string;
  /** Omitted on the final entry — an entry with no `anyOf` is always
   *  reachable, which is what guarantees the walk terminates. */
  anyOf?: PermissionString[];
}

const ADMIN_LANDING_ROUTES: ModeLandingRoute[] = [
  { path: '/admin/overview', anyOf: [Permission.READ_DASHBOARDS, Permission.READ_ALL] },
  { path: '/admin/bookings', anyOf: [Permission.READ_BOOKINGS, Permission.READ_ALL] },
  { path: '/admin/customers', anyOf: [Permission.READ_CUSTOMERS, Permission.READ_ALL] },
  { path: '/admin/gift-cards', anyOf: [Permission.READ_GIFT_CARDS] },
  { path: '/admin/marketing', anyOf: [Permission.READ_PROMOTIONS] },
  { path: '/admin/staff' },
];

/** Where a mode opens. `hasAnyPermission` is `usePermissions().hasAnyPermission`
 *  — callers own fetching it so this stays a pure function to unit-test. */
export function modeHomeFor(mode: ViewMode, hasAnyPermission: HasAnyPermission): string {
  if (mode === 'teacher') return MODE_HOME.teacher;
  const hit = ADMIN_LANDING_ROUTES.find((r) => !r.anyOf || hasAnyPermission(...r.anyOf));
  // Unreachable in practice — the last entry has no `anyOf` — but `.find` is
  // typed as possibly `undefined`, and falling back beats a crash.
  return hit?.path ?? '/admin/staff';
}
